import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import logo from "../../assets/logoProd.png";
import logoLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";

const ForgotPasswordScreen = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { forgotPassword } = useAuth();
  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleInputChange = (value) => {
    setEmail(value);
    // Очищаємо помилку при введенні
    if (errors.email) {
      setErrors((prev) => ({
        ...prev,
        email: undefined,
      }));
    }
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setErrors({ email: t("emailRequired") || "Введіть email" });
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: t("emailInvalid") || "Некорректний email" });
      return false;
    }
    return true;
  };

  const handleForgotPassword = async () => {
    if (!validateEmail()) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await forgotPassword(email.trim());
      
      if (result.success) {
        // Зберігаємо email для сторінки скидання пароля
        localStorage.setItem("resetPasswordEmail", email.trim());
        navigate("/reset-password", { 
          state: { email: email.trim() } 
        });
      } else {
        setErrors({ 
          general: result.error || t("forgotPasswordFailed") || "Помилка відправки коду" 
        });
      }
    } catch (error) {
      setErrors({ 
        general: t("forgotPasswordFailed") || "Помилка відправки коду" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleForgotPassword();
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: theme.background,
    padding: isMobile ? "8px 0" : "32px 0",
    boxSizing: "border-box",
  };

  const formStyle = {
    width: isMobile ? "96vw" : 400,
    maxWidth: 400,
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    padding: isMobile ? "16px 8px" : "32px",
    boxSizing: "border-box",
  };

  const titleStyles = {
    fontSize: "26px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "16px",
    color: theme.text,
  };

  const subtitleStyles = {
    fontSize: "16px",
    textAlign: "center",
    marginBottom: "32px",
    color: theme.textSecondary || theme.text,
    opacity: 0.8,
  };

  const formStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginBottom: "24px",
    alignItems: "center",
  };

  const linkStyles = {
    textAlign: "center",
    color: theme.primary,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "16px",
    width: "100%",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const logoStyles = {
    display: "block",
    margin: "0 auto 24px auto",
    width: "250px",
    height: "auto",
  };

  const isDark = theme.isDark;
  const logoToShow = isDark ? logoLight : logo;

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <img src={logoToShow} alt="Logo" style={logoStyles} />
        <h1 style={titleStyles}>{t("forgotPassword") || "Забули пароль?"}</h1>
        
        <p style={subtitleStyles}>
          {t("forgotPasswordDesc") || "Введіть вашу електронну пошту, і ми надішлемо вам код для скидання паролю"}
        </p>

        {errors.general && <div style={errorStyles}>{errors.general}</div>}

        <div style={formStyles}>
          <Input
            label={t("email") || "Електронна пошта"}
            type="email"
            placeholder={t("email") || "Електронна пошта"}
            value={email}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.email}
            required
            autoComplete="email"
          />

          <Button
            className="forgot-password-button"
            onClick={handleForgotPassword}
            loading={loading}
            disabled={!email.trim()}
            size={isMobile ? "small" : "medium"}
            style={{ 
              whiteSpace: "nowrap",
              width: "auto",
              minWidth: "200px",
              padding: "12px 32px"
            }}
          >
            {t("sendResetCode") || "Відправити код"}
          </Button>
        </div>

        <div style={linkStyles} onClick={handleBackToLogin}>
          ← {t("backToLogin") || "Повернутися до входу"}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;